import React, { forwardRef, useState, Children, isValidElement, useRef, useMemo, cloneElement } from 'react';
import { createMemoStyles, visuallyHidden, defaultFonts, safeRest, usePreviousState, uniqueId, getDisplayName, KEYS } from '@wds/utils';
import { Box } from '@wds/box';
import { List } from '@wds/list';
import { IconChevronUp, IconChevronDown } from '@wds/icons';
import { Typography } from '@wds/typography';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var objectWithoutProperties = {exports: {}};

var objectWithoutPropertiesLoose = {exports: {}};

(function (module) {
	function _objectWithoutPropertiesLoose(source, excluded) {
	  if (source == null) return {};
	  var target = {};
	  var sourceKeys = Object.keys(source);
	  var key, i;

	  for (i = 0; i < sourceKeys.length; i++) {
	    key = sourceKeys[i];
	    if (excluded.indexOf(key) >= 0) continue;
	    target[key] = source[key];
	  }

	  return target;
	}

	module.exports = _objectWithoutPropertiesLoose;
	module.exports["default"] = module.exports, module.exports.__esModule = true;
} (objectWithoutPropertiesLoose));

(function (module) {
	var objectWithoutPropertiesLoose$1 = objectWithoutPropertiesLoose.exports;

	function _objectWithoutProperties(source, excluded) {
	  if (source == null) return {};
	  var target = objectWithoutPropertiesLoose$1(source, excluded);
	  var key, i;

	  if (Object.getOwnPropertySymbols) {
	    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

	    for (i = 0; i < sourceSymbolKeys.length; i++) {
	      key = sourceSymbolKeys[i];
	      if (excluded.indexOf(key) >= 0) continue;
	      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
	      target[key] = source[key];
	    }
	  }

	  return target;
	}

	module.exports = _objectWithoutProperties;
	module.exports["default"] = module.exports, module.exports.__esModule = true;
} (objectWithoutProperties));

var _objectWithoutProperties = /*@__PURE__*/getDefaultExportFromCjs(objectWithoutProperties.exports);

var defineProperty = {exports: {}};

(function (module) {
	function _defineProperty(obj, key, value) {
	  if (key in obj) {
	    Object.defineProperty(obj, key, {
	      value: value,
	      enumerable: true,
	      configurable: true,
	      writable: true
	    });
	  } else {
	    obj[key] = value;
	  }

	  return obj;
	}

	module.exports = _defineProperty;
	module.exports["default"] = module.exports, module.exports.__esModule = true;
} (defineProperty));

var _defineProperty = /*@__PURE__*/getDefaultExportFromCjs(defineProperty.exports);

var _excluded$1 = ["palette", "spacing"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var useStyles = createMemoStyles(function (_ref) {
  var palette = _ref.palette,
      spacing = _ref.spacing,
      theme = _objectWithoutProperties(_ref, _excluded$1);

  var pane = {
    bgColor: palette.background.accent,
    padding: spacing[5]
  };
  return {
    wrenchPane: function wrenchPane(_ref2) {
      var hide = _ref2.hide,
          transparent = _ref2.transparent;
      return _objectSpread(_objectSpread(_objectSpread({}, hide && visuallyHidden), defaultFonts(theme)), {}, {
        background: transparent ? 'none' : pane.bgColor
      });
    },
    wrenchPaneBody: function wrenchPaneBody() {
      return {
        padding: pane.padding
      };
    }
  };
});

var _excluded = ["children", "hide", "id", "padding", "labelledBy", "role", "transparent"];

var Pane = function Pane(_ref) {
  var children = _ref.children,
      hide = _ref.hide,
      id = _ref.id,
      padding = _ref.padding,
      labelledBy = _ref.labelledBy,
      role = _ref.role,
      transparent = _ref.transparent,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _useStyles = useStyles({
    hide: hide,
    transparent: transparent,
    theme: theme
  }),
      wrenchPane = _useStyles.wrenchPane,
      wrenchPaneBody = _useStyles.wrenchPaneBody;

  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchPane,
    role: role,
    id: id,
    "aria-labelledby": labelledBy,
    "aria-hidden": hide
  }, safeRest(rest)), padding ? /*#__PURE__*/React.createElement("div", {
    className: wrenchPaneBody
  }, children) : children);
};

Pane.displayName = "Pane";
Pane.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  hide: PropTypes.bool,
  id: PropTypes.string,
  padding: PropTypes.bool,
  labelledBy: PropTypes.string,
  role: PropTypes.string,
  transparent: PropTypes.bool
};
Pane.defaultProps = {
  hide: false,
  id: undefined,
  padding: false,
  labelledBy: undefined,
  role: undefined,
  transparent: false
};
var Pane$1 = Pane;

var AccordionItem = forwardRef(function (_a, ref) {
    var addOnLeft = _a.addOnLeft, addOnRight = _a.addOnRight, affordance = _a.affordance, ariaControls = _a.ariaControls, onKeyDown = _a.onKeyDown, children = _a.children, color = _a.color, disabled = _a.disabled, expanded = _a.expanded, id = _a.id, interactive = _a.interactive, marked = _a.marked, onClick = _a.onClick, paneComponent = _a.paneComponent, upgrade = _a.upgrade;
    var tabIndex = disabled ? '-1' : 0;
    return (React.createElement(List.Item, { accordion: true, addOnLeft: addOnLeft, addOnRight: addOnRight, affordance: affordance, "aria-controls": ariaControls, onKeyDown: onKeyDown, color: color, disabled: disabled, expanded: expanded, id: id, interactive: interactive, marked: marked, onClick: onClick, paneComponent: paneComponent, role: "button", upgrade: upgrade, tabIndex: tabIndex, ref: ref }, children));
});
AccordionItem.defaultProps = {
    addOnLeft: undefined,
    addOnRight: undefined,
    affordance: undefined,
    ariaControls: undefined,
    onKeyDown: undefined,
    children: undefined,
    color: undefined,
    disabled: false,
    expanded: false,
    id: undefined,
    interactive: false,
    marked: false,
    onClick: undefined,
    paneComponent: undefined,
    upgrade: false,
    subtitle: undefined,
    titleSize: 'md',
};
AccordionItem.displayName = 'AccordionItem';

var Accordion = function (_a) {
    var multiple = _a.multiple, interactive = _a.interactive, noRules = _a.noRules, dense = _a.dense, transparent = _a.transparent, onChange = _a.onChange, id = _a.id, children = _a.children, rest = __rest(_a, ["multiple", "interactive", "noRules", "dense", "transparent", "onChange", "id", "children"]);
    var _b = useState(Children.map(children, function (child, index) {
        return isValidElement(child) && child.props.expanded ? index : null;
    }).filter(function (val) { return val !== null; })), activeItems = _b[0], setActiveItems = _b[1];
    var prevActiveItems = usePreviousState(activeItems);
    var activeItemsHasChanged = prevActiveItems !== activeItems;
    var accListRef = useRef(null);
    var uniqueAccName = useMemo(function () { return uniqueId('wrench-acc-'); }, []);
    var uniqueAccPaneName = useMemo(function () { return uniqueId('wrench-accPane-'); }, []);
    /**
     * @param {number} ind
     * @return {void}
     */
    var handleOnChange = function (evt, index) {
        // Since dom node is a div, we can't check the disabled property
        if (evt.currentTarget !== null && evt.currentTarget.tabIndex > -1) {
            var newActiveItems = [];
            if (!multiple) {
                newActiveItems = activeItems[0] === index ? [] : [index];
            }
            else if (activeItems.includes(index)) {
                newActiveItems = activeItems.filter(function (item) { return item !== index; });
            }
            else {
                newActiveItems = activeItems.concat(index);
            }
            setActiveItems(newActiveItems);
            if (onChange) {
                var currentTargetIndex = newActiveItems[newActiveItems.length - 1];
                onChange(activeItems, currentTargetIndex);
            }
        }
    };
    /**
   * Accordion according to Accordion Design Pattern in WAI-ARIA Authoring Practices 1.1
   * see https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
   *
   * Keyboard Support:
   *
  ```
  Space or Enter
    When focus is on the accordion header of a collapsed section, expands the section.
  Tab
    Moves focus to the next focusable element.
    All focusable elements in the accordion are included in the page Tab sequence.
  Shift + Tab
    Moves focus to the previous focusable element.
    All focusable elements in the accordion are included in the page Tab sequence.
  Down Arrow
    When focus is on an accordion header, moves focus to the next accordion header.
    When focus is on last accordion header, moves focus to first accordion header.
  Up Arrow
    When focus is on an accordion header, moves focus to the previous accordion header.
    When focus is on first accordion header, moves focus to last accordion header.
  Home
    When focus is on an accordion header, moves focus to the first accordion header.
  End
    When focus is on an accordion header, moves focus to the last accordion header.
  ```
   */
    var handleKeyDown = function (evt, ind) {
        if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
            evt.preventDefault();
            handleOnChange(evt, ind);
            return;
        }
        var accList = accListRef.current
            ? Array.from(accListRef.current.children).map(function (acc) {
                return acc.firstChild;
            })
            : [];
        var accListLen = accList.length - 1;
        var newFocusTarget = null;
        switch (evt.key) {
            case KEYS.DOWN:
                evt.preventDefault();
                newFocusTarget = ind < accListLen ? accList[ind + 1] : accList[0];
                break;
            case KEYS.UP:
                evt.preventDefault();
                newFocusTarget = ind === 0 ? accList[accListLen] : accList[ind - 1];
                break;
            case KEYS.HOME:
                evt.preventDefault();
                newFocusTarget = accList[0];
                break;
            case KEYS.END:
                evt.preventDefault();
                newFocusTarget = accList[accListLen];
                break;
        }
        // Guard Null Case
        if (newFocusTarget !== null) {
            newFocusTarget.focus();
        }
    };
    /**
     * @param {node} paneChildren
     * @param {string} labelledBy
     * @param {string} paneId
     * @return {node}
     */
    var renderPane = function (paneChildren, labelledBy, paneId, expanded) {
        var padding = true;
        if (getDisplayName(paneChildren) === 'List') {
            padding = false;
        }
        var paneProps = {
            padding: padding,
            labelledBy: labelledBy,
            hide: !expanded,
            role: 'region',
            id: paneId,
        };
        return (React.createElement(Pane$1, __assign({ transparent: true }, paneProps), paneChildren));
    };
    /**
     *
     * @param {node} child
     * @param {number} index
     * @param {boolean} expanded
     * @return {node}
     */
    var renderAccordionElement = function (child, index, expanded) {
        var _a, _b;
        var accordionName = (_a = child.props.id) !== null && _a !== void 0 ? _a : "".concat(uniqueAccName, "-").concat(index);
        var accordionPaneName = (_b = child.props.ariaControls) !== null && _b !== void 0 ? _b : "".concat(uniqueAccPaneName, "-").concat(index);
        var paneComponent = renderPane(child.props.children, accordionName, accordionPaneName, expanded);
        var titleVariant = child.props.titleSize === 'lg' ? 'cardTitle' : 'body';
        var childElements = (React.createElement(Box, { my: 4 },
            React.createElement(Box, { mb: 2 },
                React.createElement(Typography, { component: "strong", variant: titleVariant }, child.props.title)),
            child.props.subtitle && React.createElement(Typography, { variant: "bodySm", color: "secondary" }, child.props.subtitle)));
        return cloneElement(child, {
            children: childElements,
            expanded: expanded,
            key: accordionName,
            id: accordionName,
            // collapsed: !expanded,
            ariaControls: accordionPaneName,
            paneComponent: paneComponent,
            affordance: expanded ? (React.createElement(IconChevronUp, { title: "Chevron Up" })) : (React.createElement(IconChevronDown, { title: "Chevron Down" })),
            onClick: function (evt) { return handleOnChange(evt, index); },
            onKeyDown: function (evt) {
                return handleKeyDown(evt, index);
            },
            // multiple: multipleElement,
        });
    };
    var accordionList = Children.map(children, function (child, index) {
        var isExpandedItem = activeItems.includes(index);
        // check if a child prop changed and update state accordiongly
        var expandedProp = child ? child.props.expanded : null;
        var childExpandedChanged = isExpandedItem !== expandedProp;
        var onlyTheChildPropChanged = !activeItemsHasChanged && childExpandedChanged;
        if (onlyTheChildPropChanged) {
            setActiveItems(Children.map(children, function (c, i) {
                return isValidElement(c) && c.props.expanded ? i : null;
            }).filter(function (val) { return val !== null; }));
        }
        if (!isValidElement(child)) {
            return null;
        }
        return getDisplayName(child) === 'ListItem' ? child : renderAccordionElement(child, index, isExpandedItem);
    });
    return (React.createElement(List, __assign({ ref: accListRef, noRules: noRules, interactive: interactive, dense: dense, transparent: transparent, id: id }, safeRest(rest)), accordionList));
};
Accordion.defaultProps = {
    multiple: false,
    onChange: null,
    interactive: false,
    id: null,
    dense: false,
    transparent: false,
    noRules: false,
};
Accordion.Item = AccordionItem;

export { Accordion };
